import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import {
    CheckCircle2,
    Clock,
    ArrowRight,
    AlertTriangle,
    Shield,
    Users,
    Lightbulb,
    Calendar,
} from 'lucide-react';
import { Button } from '../ui/Button';
import styles from './StructuredContentPage.module.css';

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.15 } },
};

export interface ValueCard {
    icon: React.ComponentType<{ size?: number | string; className?: string }>;
    title: string;
    desc: string;
    gradient: string;
}

export interface Capability {
    icon: React.ComponentType<{ size?: number | string; className?: string }>;
    title: string;
    desc: string;
}

export interface TimelinePhase {
    phase: string;
    weeks: string;
    items: string[];
    validation?: string[];
    deliverables?: string[];
}

export interface RiskItem {
    category: string;
    items: string[];
}

export interface NextStep {
    title: string;
    duration: string;
    desc: string;
}

export interface BenefitItem {
    title: string;
    desc: string;
}

export interface TechStackItem {
    icon: React.ComponentType<{ size?: number | string; className?: string }>;
    name: string;
    desc?: string;
    benefits?: string[];
}

export interface EnablementItem {
    title: string;
    desc: string;
    items: string[];
}

export interface StructuredContentPageProps {
    eyebrow: string;
    title: string;
    subtitle: string;
    heroImage: string;
    // Context & Mission
    contextTitle?: string;
    contextDesc: string;
    // Operational Reality
    operationalReality: string[];
    // What we deliver
    deliverables: string[];
    // Architecture Blueprint
    architectureBlueprint?: {
        title: string;
        description: string;
        image: string;
        bullets?: string[];
    };
    // Capabilities
    capabilities: Capability[];
    // Tech Stack
    techStack?: TechStackItem[];
    // Timeline
    timeline: TimelinePhase[];
    // Risk & Compliance
    riskCompliance: RiskItem[];
    // Enablement
    enablement: string[];
    detailedEnablement?: EnablementItem[];
    // Benefits
    benefits?: BenefitItem[];
    // Section Tags (Optional overrides)
    realityTag?: string;
    deliverablesTag?: string;
    deliverablesTitle?: string;
    riskTag?: string;
    benefitsTag?: string;
    capabilitiesTitle?: string;
    // Next Steps
    nextSteps: NextStep[];
}

export const StructuredContentPage: React.FC<StructuredContentPageProps> = ({
    eyebrow,
    title,
    subtitle,
    heroImage,
    contextTitle = 'Context & Mission Impact',
    contextDesc,
    operationalReality,
    deliverables,
    architectureBlueprint,
    capabilities,
    techStack,
    timeline,
    riskCompliance,
    enablement,
    detailedEnablement,
    benefits,
    realityTag = 'Operative Realität',
    deliverablesTag = 'What Integrova Delivers',
    deliverablesTitle,
    riskTag = 'Risk, Compliance & Assurance',
    benefitsTag = 'Mehrwert',
    nextSteps,
    capabilitiesTitle,
}) => {
    const { language } = useLanguage();

    // Translations for static content
    const t = {
        freeConsultation: language === 'en' ? 'Free Consultation' : 'Kostenlose Beratung',
        getInTouch: language === 'en' ? 'Get in Touch' : 'Kontakt aufnehmen',
        challenges: language === 'en' ? 'The Challenges' : 'Die Herausforderungen',
        ourServices: language === 'en' ? 'Our Services' : 'Unsere Leistungen',
        technicalBlocks: language === 'en' ? 'Technical Building Blocks' : 'Technische Bausteine',
        techStack: language === 'en' ? 'Our Tech Stack' : 'Unser Tech Stack',
        approach: language === 'en' ? 'Our Approach' : 'Unser Vorgehen',
        riskManagement: language === 'en' ? 'Risk Management & Compliance' : 'Risikomanagement & Compliance',
        beneficiaries: language === 'en' ? 'Who Benefits?' : 'Wer profitiert?',
        nextStepsTitle: language === 'en' ? 'Next Steps' : 'Nächste Schritte',
        readyForNext: language === 'en' ? 'Ready for the next step?' : 'Bereit für den nächsten Schritt?',
        letsWork: language === 'en'
            ? "Let's tackle your challenges together – with experience, structure, and clear results."
            : 'Lassen Sie uns gemeinsam Ihre Herausforderungen angehen – mit Erfahrung, Struktur und klaren Ergebnissen.',
        requestConsultation: language === 'en' ? 'Request Consultation Now' : 'Jetzt Beratung anfragen',
        technology: language === 'en' ? 'Technology' : 'Technologie',
        deliveryModel: language === 'en' ? 'Delivery Model' : 'Delivery Model',
        sustainability: language === 'en' ? 'Sustainability' : 'Sustainability',
        enablementOwnership: language === 'en' ? 'Enablement & Ownership' : 'Enablement & Ownership',
        outputs: language === 'en' ? 'Outputs:' : 'Outputs:',
        validation: language === 'en' ? 'Validation:' : 'Validation:',
        architectureBlueprint: language === 'en' ? 'Standard architecture blueprint' : 'Standard Architektur-Blueprint',
    };

    return (
        <div className={styles.page}>
            {/* Hero Section */}
            <section
                className={styles.hero}
                style={{ backgroundImage: `url(${heroImage})` }}
            >
                <div className={styles.hero__overlay} />
                <motion.div
                    className={styles.hero__content}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <span className={styles.hero__badge}>{eyebrow}</span>
                    <h1 className={styles.hero__title}>{title}</h1>
                    <p className={styles.hero__subtitle}>{subtitle}</p>
                    <div className={styles.hero__ctas}>
                        <Button variant="primary" size="lg" icon={<ArrowRight size={20} />} to="/consultation">
                            {t.freeConsultation}
                        </Button>
                        <Button variant="secondary" size="lg" to="/contact">
                            {t.getInTouch}
                        </Button>
                    </div>
                </motion.div>
            </section>

            {/* Context & Mission Section */}
            <section className={styles.contextSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.contextCard}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className={styles.contextCard__header}>
                            <Lightbulb size={28} className={styles.contextCard__icon} />
                            <h2 className={styles.contextCard__title}>{contextTitle}</h2>
                        </div>
                        <p className={styles.contextCard__desc}>{contextDesc}</p>
                    </motion.div>
                </div>
            </section>

            {/* Benefits Section */}
            {benefits && (
                <section className={styles.benefitsSection}>
                    <div className={styles.container}>
                        <motion.div
                            className={styles.sectionHeader}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className={styles.sectionTag}>{benefitsTag}</span>
                            <h2 className={styles.sectionTitle}>{t.beneficiaries}</h2>
                        </motion.div>

                        <motion.div
                            className={styles.benefitsGrid}
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {benefits.map((item, index) => (
                                <motion.div
                                    key={index}
                                    className={styles.benefitCard}
                                    variants={itemVariants}
                                >
                                    <h3 className={styles.benefitCard__title}>{item.title}</h3>
                                    <p className={styles.benefitCard__desc}>{item.desc}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Operational Reality Section */}
            <section className={styles.realitySection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.sectionHeader}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className={styles.sectionTag}>{realityTag}</span>
                        <h2 className={styles.sectionTitle}>{t.challenges}</h2>
                    </motion.div>

                    <motion.div
                        className={styles.realityGrid}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {operationalReality.map((item, index) => (
                            <motion.div
                                key={index}
                                className={styles.realityCard}
                                variants={itemVariants}
                                style={{ flexDirection: index % 2 === 0 ? 'row' : 'row-reverse' }}
                            >
                                <AlertTriangle size={20} className={styles.realityCard__icon} />
                                <p className={styles.realityCard__text} style={{ textAlign: index % 2 === 0 ? 'left' : 'right' }}>{item}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* What We Deliver Section */}
            <section className={styles.deliverablesSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.sectionHeader}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className={styles.sectionTag}>{deliverablesTag}</span>
                        <h2 className={styles.sectionTitle}>{deliverablesTitle || t.ourServices}</h2>
                    </motion.div>

                    <motion.div
                        className={styles.deliverablesGrid}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {deliverables.map((item, index) => (
                            <motion.div
                                key={index}
                                className={styles.deliverableCard}
                                variants={itemVariants}
                            >
                                <CheckCircle2 size={22} className={styles.deliverableCard__icon} />
                                <p className={styles.deliverableCard__text}>{item}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {architectureBlueprint && (
                <section className={styles.blueprintSection}>
                    <div className={styles.container}>
                        <motion.div
                            className={styles.sectionHeader}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className={styles.sectionTag}>{t.architectureBlueprint}</span>
                            <h2 className={styles.sectionTitle}>{architectureBlueprint.title}</h2>
                        </motion.div>

                        <motion.div
                            className={styles.blueprintLayout}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className={styles.blueprintMedia}>
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={`${architectureBlueprint.image.replace(/\.(png|jpg|jpeg)$/i, '')}-800.webp 800w, ${architectureBlueprint.image.replace(/\.(png|jpg|jpeg)$/i, '')}-1600.webp 1600w`}
                                        sizes="(max-width: 900px) 100vw, 520px"
                                    />
                                    <img
                                        src={architectureBlueprint.image}
                                        alt={architectureBlueprint.title}
                                        className={styles.blueprintImage}
                                        loading="lazy"
                                    />
                                </picture>
                            </div>
                            <div className={styles.blueprintContent}>
                                <p className={styles.blueprintDesc}>{architectureBlueprint.description}</p>
                                {architectureBlueprint.bullets && (
                                    <ul className={styles.blueprintList}>
                                        {architectureBlueprint.bullets.map((item) => (
                                            <li key={item}>
                                                <span className={styles.blueprintDot} />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Capabilities Grid */}
            <section className={styles.capabilitiesSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.sectionHeader}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className={styles.sectionTag}>{t.technicalBlocks}</span>
                        <h2 className={styles.sectionTitle}>{capabilitiesTitle || t.technicalBlocks}</h2>
                    </motion.div>

                    <motion.div
                        className={styles.capabilitiesGrid}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {capabilities.map((cap, index) => {
                            const Icon = cap.icon;
                            return (
                                <motion.div
                                    key={index}
                                    className={styles.capabilityCard}
                                    variants={itemVariants}
                                >
                                    <div className={styles.capabilityCard__iconWrapper}>
                                        <Icon size={32} />
                                    </div>
                                    <div className={styles.capabilityCard__content}>
                                        <h3 className={styles.capabilityCard__title}>{cap.title}</h3>
                                        <p className={styles.capabilityCard__desc}>{cap.desc}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* Tech Stack Section */}
            {techStack && techStack.length > 0 && (
                <section className={styles.techStackSection}>
                    <div className={styles.container}>
                        <motion.div
                            className={styles.sectionHeader}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className={styles.sectionTag}>{t.technology}</span>
                            <h2 className={styles.sectionTitle}>{t.techStack}</h2>
                        </motion.div>

                        <motion.div
                            className={styles.techStackGrid}
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {techStack.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        className={`${styles.techStackCard} ${item.desc ? styles.techStackCard__detailed : ''}`}
                                        variants={itemVariants}
                                        whileHover={{ y: -4, transition: { duration: 0.2 } }}
                                    >
                                        <div className={styles.techStackCard__header}>
                                            <div className={styles.techStackCard__iconWrapper}>
                                                <Icon size={32} />
                                            </div>
                                            <span className={styles.techStackCard__name}>{item.name}</span>
                                        </div>
                                        {item.desc && (
                                            <div className={styles.techStackCard__details}>
                                                <p className={styles.techStackCard__desc}>{item.desc}</p>
                                                {item.benefits && (
                                                    <ul className={styles.techStackCard__benefits}>
                                                        {item.benefits.map((benefit, i) => (
                                                            <li key={i}>{benefit}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Timeline Section */}
            <section className={styles.timelineSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.sectionHeader}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className={styles.sectionTag}>{t.deliveryModel}</span>
                        <h2 className={styles.sectionTitle}>{t.approach}</h2>
                    </motion.div>

                    <motion.div
                        className={styles.timeline}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {timeline.map((step, index) => (
                            <motion.div
                                key={index}
                                className={styles.timeline__step}
                                variants={itemVariants}
                            >
                                <div className={styles.timeline__marker}>
                                    <span className={styles.timeline__num}>{String(index + 1).padStart(2, '0')}</span>
                                    {index < timeline.length - 1 && <div className={styles.timeline__line} />}
                                </div>
                                <div className={styles.timeline__card}>
                                    <div className={styles.timeline__header}>
                                        <h3 className={styles.timeline__phase}>{step.phase}</h3>
                                        <span className={styles.timeline__weeks}>
                                            <Clock size={14} />
                                            {step.weeks}
                                        </span>
                                    </div>
                                    <div className={styles.timeline__content}>
                                        <div className={styles.timeline__outputs}>
                                            <h4 className={styles.timeline__outputsTitle}>{t.outputs}</h4>
                                            <ul className={styles.timeline__items}>
                                                {step.items.map((item, i) => (
                                                    <li key={i}>
                                                        <CheckCircle2 size={16} />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        {step.validation && step.validation.length > 0 && (
                                            <div className={styles.timeline__validation}>
                                                <h4 className={styles.timeline__validationTitle}>{t.validation}</h4>
                                                <ul className={styles.timeline__items}>
                                                    {step.validation.map((item, i) => (
                                                        <li key={i}>
                                                            <Shield size={16} />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Risk & Compliance Section */}
            <section className={styles.riskSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.sectionHeader}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className={styles.sectionTag}>{riskTag}</span>
                        <h2 className={styles.sectionTitle}>{t.riskManagement}</h2>
                    </motion.div>

                    <motion.div
                        className={styles.riskGrid}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {riskCompliance.map((risk, index) => (
                            <motion.div
                                key={index}
                                className={styles.riskCard}
                                variants={itemVariants}
                            >
                                <h3 className={styles.riskCard__title}>
                                    <Shield size={20} />
                                    {risk.category}
                                </h3>
                                <ul className={styles.riskCard__list}>
                                    {risk.items.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Enablement Section */}
            <section className={styles.enablementSection}>
                <div className={styles.container}>
                    {detailedEnablement ? (
                        <>
                            <motion.div
                                className={styles.sectionHeader}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <span className={styles.sectionTag}>{t.sustainability}</span>
                                <h2 className={styles.sectionTitle}>{t.enablementOwnership}</h2>
                            </motion.div>
                            <div className={styles.detailedEnablementGrid}>
                                {detailedEnablement.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        className={styles.detailedEnablementCard}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <h3 className={styles.detailedEnablementCard__title}>{item.title}</h3>
                                        <p className={styles.detailedEnablementCard__desc}>{item.desc}</p>
                                        <ul className={styles.detailedEnablementCard__list}>
                                            {item.items.map((subItem, i) => (
                                                <li key={i}>
                                                    <CheckCircle2 size={16} />
                                                    {subItem}
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <motion.div
                            className={styles.enablementCard}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className={styles.enablementCard__header}>
                                <Users size={28} className={styles.enablementCard__icon} />
                                <h2 className={styles.enablementCard__title}>{t.enablementOwnership}</h2>
                            </div>
                            <ul className={styles.enablementCard__list}>
                                {enablement.map((item, index) => (
                                    <li key={index}>
                                        <CheckCircle2 size={18} />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Next Steps Section */}
            <section className={styles.nextStepsSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.sectionHeader}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className={styles.sectionTag}>{t.nextStepsTitle}</span>
                        <h2 className={styles.sectionTitle}>{t.nextStepsTitle}</h2>
                    </motion.div>

                    <motion.div
                        className={styles.nextStepsGrid}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {nextSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                className={styles.nextStepCard}
                                variants={itemVariants}
                                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                            >
                                <div className={styles.nextStepCard__header}>
                                    <Calendar size={20} className={styles.nextStepCard__icon} />
                                    <span className={styles.nextStepCard__duration}>{step.duration}</span>
                                </div>
                                <h3 className={styles.nextStepCard__title}>{step.title}</h3>
                                <p className={styles.nextStepCard__desc}>{step.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className={styles.ctaSection}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.ctaCard}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className={styles.ctaCard__title}>{t.readyForNext}</h2>
                        <p className={styles.ctaCard__desc}>
                            {t.letsWork}
                        </p>
                        <div className={styles.ctaCard__buttons}>
                            <Button variant="primary" size="lg" icon={<ArrowRight size={20} />} to="/consultation">
                                {t.requestConsultation}
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default StructuredContentPage;
